import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, InputNumber, message, Space, Alert, Image, Typography, Row, Col, Divider, Select } from "antd";
import { CalculatorOutlined, TableOutlined, LineChartOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { Option } = Select;

const XBarCharts = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [inputData, setInputData] = useState({
    title: "x-bar Charts",
    m: 3,
    n: 5,
    choice: 1,
    method_name: "3 sigma (99.7%)",
    means: [],
    ranges: []
  });
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const getAuthToken = () => localStorage.getItem("access_token");

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    window.location.reload();
  };

  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else if (initialConfig) {
      const { m, n, choice, method_name } = initialConfig;
      setInputData({
        ...inputData,
        m: m || 3,
        n: n || 5,
        choice: choice || 1,
        method_name: method_name || "3 sigma (99.7%)",
        means: Array(m || 3).fill(0),
        ranges: Array(m || 3).fill(0)
      });
      setResults(null);
      setIsSaved(false);
      setActiveTab("input");
    }
  }, [fileId, initialConfig]);

  const loadData = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      const res = await axios.get(
        `/api/retrieve_xBarCharts/${id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setInputData(res.data.input_data);
      setResults(res.data.output_data || res.data); // Adjusting for response format
      setIsSaved(true);
      setActiveTab("output");
    } catch (err) {
      console.error("Load error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const saveData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");

      setIsLoading(true);

      const payload = {
        name: fileName || "Untitled Analysis",
        input_data: inputData,
      };

      let res;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (fileId) {
        res = await axios.put(
          `/api/update_xBarCharts/${fileId}/`,
          payload,
          config
        );
      } else {
        res = await axios.post(
          `/api/save_xBarCharts/`,
          payload,
          config
        );
      }

      setResults(res.data.output_data || res.data);

      // Update selectedFile.id immediately after saving
      if (!fileId && setSelectedFile) {
        setSelectedFile({
          id: res.data.id,
          name: res.data.name,
          type: "x_bar_charts",
          inputData: res.data.input_data
        });
      }

      setIsSaved(true);
      setActiveTab("output");
      message.success(fileId ? "Updated successfully" : "Saved successfully");
    } catch (err) {
      console.error("Save error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error(`Failed to save: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataChange = (index, field, val) => {
    const newData = [...inputData[field]];
    newData[index] = val;
    setInputData({ ...inputData, [field]: newData });
  };

  const renderInputForm = () => {
    const dataSource = Array.from({ length: inputData.m }, (_, i) => ({
      key: i,
      sample: i + 1,
      mean: inputData.means?.[i] || 0,
      range: inputData.ranges?.[i] || 0
    }));

    const columns = [
      { title: 'Sample (m)', dataIndex: 'sample', key: 'sample' },
      {
        title: 'Mean (X-bar)',
        dataIndex: 'mean',
        key: 'mean',
        render: (text, record, index) => (
          <InputNumber
            value={record.mean}
            onChange={(val) => handleDataChange(index, "means", val)}
            style={{ width: '100%' }}
          />
        )
      },
      {
        title: 'Range (R)',
        dataIndex: 'range',
        key: 'range',
        render: (text, record, index) => (
          <InputNumber
            value={record.range}
            onChange={(val) => handleDataChange(index, "ranges", val)}
            style={{ width: '100%' }}
          />
        )
      }
    ];

    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <Text strong>Subgroup Size (n):</Text>
              <InputNumber
                style={{ width: '100%' }}
                min={2}
                value={inputData.n}
                onChange={(v) => setInputData({ ...inputData, n: v })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Number of Samples (m):</Text>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                value={inputData.m}
                onChange={(v) => {
                  const newM = v || 1;
                  const newMeans = [...(inputData.means || [])];
                  const newRanges = [...(inputData.ranges || [])];

                  while (newMeans.length < newM) { newMeans.push(0); }
                  while (newRanges.length < newM) { newRanges.push(0); }

                  setInputData({
                    ...inputData,
                    m: newM,
                    means: newMeans.slice(0, newM),
                    ranges: newRanges.slice(0, newM)
                  });
                }}
              />
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Method:</Text>
              <Select
                style={{ width: '100%' }}
                value={inputData.method_name}
                onChange={(val) => setInputData({ ...inputData, method_name: val })}
              >
                <Option value="3 sigma (99.7%)">3 sigma (99.7%)</Option>
                <Option value="User defined sigma">User defined sigma</Option>
              </Select>
            </Col>
          </Row>

          <Divider orientation="left">Sample Data</Divider>
          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={{ pageSize: 15 }}
            size="small"
            bordered
          />

          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            onClick={saveData}
            loading={isLoading}
            size="large"
            block
            style={{ height: '50px', marginTop: '20px' }}
          >
            {isSaved ? "Update Analysis" : "Calculate & Save"}
          </Button>
        </Space>
      </Card>
    );
  };

  const renderOutputTable = () => {
    if (!results) {
      return <Alert message="No Results" type="info" showIcon />;
    }

    // Try to extract summary or flatten keys
    let displayMetrics = [];
    if (results.summary) {
      displayMetrics = Object.entries(results.summary).map(([key, val], idx) => ({
        key: idx,
        parameter: key.replace(/_/g, " ").toUpperCase(),
        value: typeof val === 'number' ? val.toFixed(4) : String(val)
      }));
    } else {
      displayMetrics = Object.entries(results).filter(([key, val]) => typeof val !== 'object').map(([key, val], idx) => ({
        key: idx,
        parameter: key.replace(/_/g, " ").toUpperCase(),
        value: typeof val === 'number' ? val.toFixed(4) : String(val)
      }));
    }

    const columns = [
      { title: 'Parameter', dataIndex: 'parameter', key: 'parameter', render: (text) => <Text strong>{text}</Text> },
      { title: 'Value', dataIndex: 'value', key: 'value' },
    ];

    const chartUrl = results.chart_url || (results.charts && results.charts.residuals);

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={displayMetrics.length > 0 ? 12 : 24}>
            {displayMetrics.length > 0 && (
              <Card title="Analysis Summary" size="small">
                <Table
                  dataSource={displayMetrics}
                  columns={columns}
                  pagination={false}
                  size="middle"
                  bordered
                />
              </Card>
            )}
          </Col>
          <Col xs={24} md={displayMetrics.length > 0 ? 12 : 24}>
            {chartUrl && (
              <Card title="Control Chart" size="small" style={{ textAlign: 'center' }}>
                <Image
                  src={chartUrl}
                  alt="x-bar Chart"
                  fallback="https://via.placeholder.com/600x400?text=x-bar+Chart+Not+Found"
                  style={{ maxHeight: '400px' }}
                />
              </Card>
            )}
          </Col>
        </Row>
      </Space>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            <CalculatorOutlined /> {fileName || "x-bar Charts Analysis"}
          </Title>
        }
        className="calculator-card"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          items={[
            {
              key: "input",
              label: (<span><TableOutlined /> Inputs</span>),
              children: renderInputForm()
            },
            {
              key: "output",
              label: (<span><LineChartOutlined /> Results</span>),
              disabled: !results,
              children: renderOutputTable()
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default XBarCharts;
